import { v4 as uuidv4 } from 'uuid';
import * as linkify from 'linkifyjs';

interface LinkMatch {
  index: number;
  href: string;
  value: string;
}

export function matchLinks(text: string): LinkMatch[] {
  const links = linkify.find(text);

  return links.map((link) => ({
    index: link.start,
    href: link.href,
    value: link.value,
  }));
}

// Iterates through all blocks and their children, checking if they have text matching a link pattern, then return updated blocks with markDefs for the links
export const transformLinksInPortableText = (blocks: any[]) => {
  const newBody = blocks.reduce((accBlocks: any, block: any) => {
    const newMarkDefs: any[] = [];

    const newChildren = block.children.reduce(
      (accChildren: any, child: { _type: string; _key: string; text: string; marks: string[] }) => {
        // Inline objects (e.g. a "mention") have no .text - pass them through untouched.
        if (child._type !== 'span') {
          return [...accChildren, child];
        }

        const links = matchLinks(child.text);

        // No links found in text, return the child as is
        if (!links || links.length === 0) {
          return [...accChildren, child];
        }

        // We found one link, which matches the entire text, return an updated child with marks and add a new markDef
        if (links.length === 1 && links[0].value === child.text) {
          const markDefId = uuidv4();
          const newChild = {
            ...child,
            marks: [...(child.marks || []), markDefId],
          };

          newMarkDefs.push({
            _type: 'externalLink',
            _key: markDefId,
            href: links[0].href,
          });

          return [...accChildren, newChild];
        }

        // Multiple links or links embedded in text - split the text on each link
        let lastIndex = 0;
        const newChildSpans: any[] = [];

        links.forEach((link: any, linkIndex: number) => {
          const linkStart = link.index;
          const linkEnd = linkStart + link.value.length;

          // Add text before the link
          if (linkStart > lastIndex) {
            const textBefore = child.text.substring(lastIndex, linkStart);
            if (textBefore) {
              newChildSpans.push({
                ...child,
                text: textBefore,
                _key: `${child._key}-${linkIndex}-before`,
              });
            }
          }

          // Add the link with mark
          const markDefId = uuidv4();
          newMarkDefs.push({
            _type: 'externalLink',
            _key: markDefId,
            href: link.href,
          });

          newChildSpans.push({
            ...child,
            text: link.value,
            _key: `${child._key}-${linkIndex}-link`,
            marks: [...(child.marks || []), markDefId],
          });

          lastIndex = linkEnd;
        });

        // Add any remaining text after the last link
        if (lastIndex < child.text.length) {
          const textAfter = child.text.substring(lastIndex);
          if (textAfter) {
            newChildSpans.push({
              ...child,
              text: textAfter,
              _key: `${child._key}-after`,
            });
          }
        }

        return [...accChildren, ...newChildSpans];
      },
      []
    );

    return [
      ...accBlocks,
      {
        ...block,
        children: newChildren,
        // Merge any new markDefs with existing ones
        ...((block.markDefs || newMarkDefs.length > 0) && {
          markDefs: [...(block.markDefs || []), ...newMarkDefs],
        }),
      },
    ];
  }, []);

  return newBody;
};
